import { computed, Injectable, signal } from '@angular/core';
import { StoredFilter } from '../models/filter-models';
import { VERSION, LOCAL_STORAGE_KEY_ACTIVE_FILTER_NAME, LOCAL_STORAGE_KEY_FILTER_STORED, LOCAL_STORAGE_KEY_FILTERS, LOCAL_STORAGE_KEY_LAST_VERSION, LOCAL_STORAGE_KEY_DEFAULT_FILTER_ID } from '../const';
import { patchVersion } from '../../version';
import { DEFAULT_FILTER_NAME } from '../../game';
import { FilterMigratorService } from './filter-migrator.service';
import { FilterIdService } from './filter-id.service';

@Injectable({
  providedIn: 'root'
})
export class FilterStoreService {
  storedFilters!: StoredFilter[];
  activeStoredFilter = signal<StoredFilter>({} as StoredFilter);
  activeFilter = computed(() => this.activeStoredFilter().filter);
  activeFilterName = signal<string>(DEFAULT_FILTER_NAME);
  dirtyFilterChanged = signal<number>(0); // Allows a dirty way to check for a changed filter; to update styles

  constructor(private filterMigratorService: FilterMigratorService, private filterIdService: FilterIdService) {
    this.initOrMigrateFilters();

    this.storedFilters = localStorage.getItem(LOCAL_STORAGE_KEY_FILTERS) ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_FILTERS)!) : [];
    this.migrateAllStoredFilters();
    this.activeFilterName.set(localStorage.getItem(LOCAL_STORAGE_KEY_ACTIVE_FILTER_NAME) || DEFAULT_FILTER_NAME);

    const storedFilterId = localStorage.getItem(LOCAL_STORAGE_KEY_DEFAULT_FILTER_ID);
    if (storedFilterId) {
      this.setFilterId(DEFAULT_FILTER_NAME, storedFilterId);
      localStorage.removeItem(LOCAL_STORAGE_KEY_DEFAULT_FILTER_ID);
    }
  }

  /**
   * Initialize the store with a new filter if none exists. Should be called by FilterService after creating a new filter.
   */
  initializeWithNewFilter(filter: StoredFilter): void {
    if (this.storedFilters.length === 0) {
      this.storedFilters = [filter];
      this.saveFilters();
    }
  }

  setActiveFilter(name: string, selectedFilter?: StoredFilter): StoredFilter {
    const filter = selectedFilter || this.storedFilters.find(f => f.name === name) || this.storedFilters[0];

    const lastPatchVersion = filter.build || 0;
    if (lastPatchVersion < patchVersion) {
      this.filterMigratorService.migrateFilter(filter.filter, lastPatchVersion, 'fallback');
      this.updateFilterMetadata(filter);
    }

    localStorage.setItem(LOCAL_STORAGE_KEY_ACTIVE_FILTER_NAME, name);
    this.activeFilterName.set(name);
    this.activeStoredFilter.set(filter);
    this.dirtyFilterChanged.update(c => c + 1);

    return filter;
  }

  setFilterId(name: string, id: string) {
    const filter = this.storedFilters.find(f => f.name === name);
    if (filter) {
      filter.id = id;
      this.saveFilters();
    }
  }

  // Updates our internal Filter.id (share/snapshot id), not the GGG StoredFilter.id.
  updateInternalFilterId(name: string, id: string) {
    const stored = this.storedFilters.find(f => f.name === name);
    if (!stored?.filter || stored.filter.id === id) return;

    stored.filter.id = id;
    this.saveFilters();

    if (this.activeFilterName() === name) {
      this.activeStoredFilter.update(f => ({
        ...f,
        filter: { ...f.filter, id },
      }));
    }
  }

  markSnapshotUploaded(name: string) {
    const filter = this.storedFilters.find(f => f.name === name);
    if (!filter || filter.snapshotUploaded) return;

    filter.snapshotUploaded = true;
    this.saveFilters();

    if (this.activeFilterName() === name) {
      this.activeStoredFilter.update(f => ({ ...f, snapshotUploaded: true }));
    }
  }

  addFilter(filter: StoredFilter) {
    this.storedFilters = [...this.storedFilters, filter];
    this.saveFilters();
  }

  removeFilter(name: string) {
    this.storedFilters = this.storedFilters.filter(f => f.name !== name);
    this.saveFilters();
  }

  updateFilterName(oldName: string, newName: string) {
    if (this.activeFilterName() === oldName) {
      this.activeFilterName.set(newName);
      localStorage.setItem(LOCAL_STORAGE_KEY_ACTIVE_FILTER_NAME, newName);
    }
  }

  saveFilters() {
    localStorage.setItem(LOCAL_STORAGE_KEY_FILTERS, JSON.stringify(this.storedFilters));
  }

  updateFilterMetadata(filter: StoredFilter) {
    filter.lastUpdated = new Date().toISOString();
    filter.version = VERSION;
    filter.build = patchVersion;
    this.saveFilters();
  }

  private migrateAllStoredFilters(): void {
    let migrated = false;

    for (const stored of this.storedFilters) {
      const build = stored.build || 0;
      if (build < patchVersion) {
        this.filterMigratorService.migrateFilter(stored.filter, build, 'startup');
        stored.build = patchVersion;
        stored.version = VERSION;
        migrated = true;
      } else if (!stored.filter.id) {
        stored.filter.id = this.filterIdService.generateId();
        migrated = true;
      }
    }

    if (migrated) {
      this.saveFilters();
    }
  }

  private initOrMigrateFilters(): void {
    const oldFilter = localStorage.getItem('filter-v9');
    const filterSet = localStorage.getItem(LOCAL_STORAGE_KEY_FILTER_STORED);
    const lastVersion = localStorage.getItem(LOCAL_STORAGE_KEY_LAST_VERSION);
    // Parse patchVersion from version string (handles both old "0.9.132" and new "1.0.132.1" formats)
    const lastVersionBuildNumber = lastVersion ? (() => {
      const parts = lastVersion.split('.');
      // If 4 parts (new format), use 3rd part (patchVersion). If 3 parts (old format), use last part.
      return Number(parts.length === 4 ? parts[2] : parts[parts.length - 1] || '0');
    })() : patchVersion;

    if (!oldFilter && lastVersion?.startsWith('9')) {
      // Fix for fuckup where i didn't update the version number in the filter
      const oldFilterObj = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_FILTERS)!);
      oldFilterObj[0].version = lastVersion;
      oldFilterObj[0].build = lastVersionBuildNumber;
      // Will be set in constructor after this method
      localStorage.setItem(LOCAL_STORAGE_KEY_FILTERS, JSON.stringify(oldFilterObj));
    } else if (oldFilter) {
      const oldFilterObj = JSON.parse(oldFilter);
      const newFilter: StoredFilter = {
        name: DEFAULT_FILTER_NAME,
        filter: oldFilterObj,
        lastUpdated: new Date().toISOString(),
        version: lastVersion || VERSION,
        build: lastVersionBuildNumber
      };
      // Will be set in constructor after this method
      localStorage.setItem(LOCAL_STORAGE_KEY_FILTERS, JSON.stringify([newFilter]));
      for (let i = 9; i >= 1; i--) {
        localStorage.removeItem(`filter-v${i}`);
      }
    } else if (!filterSet) {
      localStorage.setItem(LOCAL_STORAGE_KEY_FILTER_STORED, '1');
    }
  }
}
